import { generateUndeterminedPerson } from './../../realtime/common/services/generate-undetermined-person';
import EventEmitter from '../../utility/event-emitter';
import _ from 'lodash';
import uuid from 'uuid/v4';
import to from 'await-to-js';
import { setCallSessionInf, setRoomInf, setIsGroupCallInf, } from '../../store/features/callInfo/action-creators';
import * as getStatsAnalyzer from './common/lib/webrtc-stats-analyzer';
import * as devices from '../../modules/devices';
import { actions as callActions, helpers as callHelpers, } from '../../modules/call';
import { selectCallInfo } from '../../store/features/callInfo/selectors';
import { AngularServices } from '../../modules/core';
import { RootScopeEvents } from '../../common/services/root-scope-types';
import { API } from '../../vendor/api';
import { shallowEqual } from 'react-redux';
import { PnEvents } from '../../realtime';
import { VideoProviderEvents } from './';
import { PeopleCollection } from '../../realtime/common/services/people-collection';
export var MediaModes;
(function (MediaModes) {
    MediaModes["ROUTED"] = "routed";
    MediaModes["RELAYED"] = "relayed";
})(MediaModes || (MediaModes = {}));
export var AudioTypes;
(function (AudioTypes) {
    AudioTypes["STANDARD"] = "standard";
    AudioTypes["MEDICAL"] = "medical";
})(AudioTypes || (AudioTypes = {}));
export default class AbstractVideoProvider extends EventEmitter {
    constructor($injector) {
        super();
        this.$injector = $injector;
        this.subscribers = [];
        this.connectionState = {};
        this.publisherErrors = [];
        this.connections = [];
        this.streams = [];
        this.cameraAccessPollInterval = null;
        this.getUserMediaLastError = null;
        this.audioType = AudioTypes.STANDARD;
        this.captionsId = null;
        this.isPublisherLoading = false;
        this.$ngRedux = $injector.get('$ngRedux');
        this.$ngRedux.subscribe(() => this.onStoreUpdate());
        this.peopleCollection = new PeopleCollection();
        this.mediaStreamTrackHelper = $injector.get('mediaStreamTrackHelper');
        this.localVideoPublisherId = `local-video-publisher-${uuid()}`;
        this.localScreenPublisherId = `local-screen-publisher-${uuid()}`;
        this.$rootScope = $injector.get('$rootScope');
        this.setSinkId = $injector.get('setSinkId');
        const VideoQualityService = $injector.get(AngularServices.VideoQualityService);
        this.$timeout = $injector.get('$timeout');
        this.$interval = $injector.get('$interval');
        this.$ngRedux = $injector.get('$ngRedux');
        this.roomHelpersFactory = $injector.get('roomHelpersFactory');
        this.pn = $injector.get(AngularServices.Pn);
        this.$log = $injector.get('$log');
        this.debug = this.$log.debug.bind(this.$log, 'video-provider:');
        this.errorHandler = $injector.get('enableCameraErrorHandler');
        this.currentVideoQuality = VideoQualityService.current;
        this.addPnEventListener(PnEvents.Join);
        this.addPnEventListener(PnEvents.Leave);
        this.addPnEventListener(PnEvents.StateUpdate);
        this.$rootScope.$on(RootScopeEvents.CHANGE_VIDEO_QUALITY, ($e, { quality }) => {
            if (this.currentVideoQuality === quality)
                return;
            this.currentVideoQuality = quality;
            this.onVideoQualityChange();
        });
        this.$rootScope.$on(RootScopeEvents.CHANGE_AUDIO_TYPE, ($e, audioType) => {
            this.changeAudioType(audioType);
        });
        this.$rootScope.$on(RootScopeEvents.GLOBAL_UI.SCREEN_SHARING_UPDATED, ($e, enable, constraints) => {
            if (enable) {
                void this.$timeout(async () => this.initScreenPublisher(constraints));
            }
            else {
                this.destroyScreenPublisher();
            }
        });
        Object.defineProperty(this, 'ui', {
            enumerable: true,
            get() {
                return this._getAudioVideoState();
            },
        });
        // set isRearCameraUsed
        this.on(VideoProviderEvents.LocalStream, async (stream) => {
            this.localStream = stream;
            this.setCurrentDevices();
            this.updateIsRearCameraUsed();
        });
        this.initialCameraIdPromise = new Promise((resolve) => {
            this.once(VideoProviderEvents.LocalStream, async (stream) => {
                if (!stream)
                    return resolve(null);
                const accessibleCameras = devices.selectors.getAccessibleCameras(this.$ngRedux.getState());
                if (!accessibleCameras.length)
                    return resolve(null);
                const firstDeviceId = accessibleCameras[0].deviceId;
                if (accessibleCameras.length === 1 || !stream.getVideoTracks()[0])
                    return resolve(firstDeviceId);
                const track = stream.getVideoTracks()[0];
                const trackDeviceId = this.mediaStreamTrackHelper.getDeviceId(track);
                let initialCamera = _.find(accessibleCameras, (cam) => cam.deviceId === trackDeviceId);
                if (!initialCamera && track.label) {
                    // track.getSettings() may not contain device id
                    // try to detect initial camera by label in this case
                    initialCamera = _.find(accessibleCameras, { label: track.label });
                }
                return resolve(initialCamera ? initialCamera.deviceId : firstDeviceId);
            });
        });
        getStatsAnalyzer.eventEmitter.on('MOSChange', (e) => {
            e.uiid = e.person && e.person.uuid;
            e.userId = this.pn.getPersonIdentityUserId(e.person);
            this.emit(VideoProviderEvents.MOSChange, e);
            this.$ngRedux.dispatch(callActions.setConnectionStatus({
                uuid: e.uiid,
                state: e.currentValue,
            }));
            delete e.person;
            this.$log.debug('MOSChange', e);
        });
        getStatsAnalyzer.eventEmitter.on('MOSUpdate', (e) => {
            e.uuid = e.person && e.person.uuid;
            e.userId = this.pn.getPersonIdentityUserId(e.person);
            this.emit(VideoProviderEvents.MOSUpdate, e);
            delete e.person;
            this.$log.debug('MOSUpdate', e);
        });
        getStatsAnalyzer.eventEmitter.on('PublisherMOSUpdate', (e) => {
            this.emit(VideoProviderEvents.PublisherMOSUpdate, e);
            this.$log.debug('PublisherMOSUpdate', e);
        });
    }
    get currentCameraId() {
        return this.currentCamera ? this.currentCamera.deviceId : null;
    }
    get currentMicId() {
        return this.currentMic ? this.currentMic.deviceId : null;
    }
    get currentSpeakerId() {
        return this.currentSpeaker ? this.currentSpeaker.deviceId : null;
    }
    // TODO: refactor & test
    /*get hasVideo() {
      if (this.localStream) {
        return this.localStream.getVideoTracks()[0].enabled;
      } else {
        return false;
      }
    }
  
    get hasAudio() {
      if (this.localStream) {
        return this.localStream.getAudioTracks()[0].enabled;
      } else {
        return false;
      }
    }*/
    /**
     * @description Call to refresh participant Stream
     */
    recreateStream(stream) {
        throw 'Not implemented';
    }
    onOriginalSoundChange() {
        this._onConfigUpdate();
    }
    onAudioConfigUpdate() {
        this.onConfigUpdate();
    }
    updateIsRearCameraUsed() {
        this.$timeout(() => {
            const [videoSource] = this.localStream.getVideoTracks();
            if (!videoSource)
                return;
            const facingMode = this.mediaStreamTrackHelper.getFacingMode(videoSource);
            this.isRearCameraUsed =
                facingMode === devices.constants.DeviceFacingMode.environment;
        });
    }
    get isInCall() {
        return this.connections.length > 0;
    }
    get isInGroupCall() {
        return this.peopleCollection.length > 1;
    }
    addPnEventListener(event) {
        const pn = this.$injector.get(AngularServices.Pn);
        pn.on(event, (person) => {
            this.connections.forEach((connection) => {
                const p = this.findPnPersonByState(connection.personSearch);
                if (person.uuid === p.uuid) {
                    if (connection.person.isUndetermined) {
                        console.error(`Connection person found on pn event ${event}`, p.uuid);
                    }
                    connection.person = p;
                    this.updatePeople();
                }
            });
        });
    }
    async setCurrentDevices() {
        const accessibleCameras = devices.selectors.getAccessibleCameras(this.$ngRedux.getState());
        const accessibleMicrophones = devices.selectors.getAccessibleMicrophones(this.$ngRedux.getState());
        const accessibleSpeakers = devices.selectors.getAccessibleSpeakers(this.$ngRedux.getState());
        const cameraId = devices.selectors.getCurrentCameraId(this.$ngRedux.getState());
        const speakerId = devices.selectors.getCurrentSpeakerId(this.$ngRedux.getState());
        const micId = devices.selectors.getCurrentMicrophoneId(this.$ngRedux.getState());
        // camera
        const videoTrack = this.localStream.getVideoTracks()[0];
        const isVideoTrackManipulated = 'MediaStreamTrackGenerator' in window &&
            videoTrack instanceof MediaStreamTrackGenerator;
        const trackDeviceId = !!videoTrack && !isVideoTrackManipulated
            ? this.mediaStreamTrackHelper.getDeviceId(videoTrack)
            : null;
        const currentCameraId = trackDeviceId || cameraId || (await this.getInitialCameraId());
        this.debug(`setCurrentDevices: currentCameraId: ${currentCameraId} trackDeviceId: ${trackDeviceId} settingsDeviceId: ${cameraId}`);
        this.currentCamera = accessibleCameras.find((camera) => camera.deviceId === currentCameraId);
        // mic
        const audioTrack = this.localStream.getAudioTracks()[0];
        const trackMicId = this.mediaStreamTrackHelper.getDeviceId(audioTrack);
        const currentMicId = trackMicId || micId || 'default';
        this.currentMic = accessibleMicrophones.find((mic) => mic.deviceId === currentMicId);
        this.debug(`setCurrentDevices: currentMicId: ${currentMicId}`);
        //speaker
        if (speakerId) {
            this.currentSpeaker = accessibleSpeakers.find((s) => s.deviceId === speakerId);
        }
        else {
            this.currentSpeaker = accessibleSpeakers.find((s) => s.deviceId === 'default');
        }
        if (!this.currentSpeaker && accessibleSpeakers.length > 0) {
            this.currentSpeaker = accessibleSpeakers[0];
        }
        this.$ngRedux.dispatch(devices.actions.updateCurrentDevices({
            cameraId: this.currentCameraId,
            microphoneId: this.currentMicId,
            speakerId: this.currentSpeakerId,
        }));
    }
    removeLocalVideoElement() {
        this.emit(VideoProviderEvents.LocalStreamDestroyed);
        const container = document.getElementById(this.localVideoPublisherId);
        if (container)
            container.querySelectorAll('video').forEach((video) => {
                video.pause();
                video.src = '';
                video.remove();
            });
    }
    updateStreams() {
        this.updatePeople();
        this.streams = _.flatten(this.connections.map((c) => c.streams));
    }
    getScreensharePublisher() {
        return null;
    }
    getScreensharePublisherVideoElement() {
        return null;
    }
    updatePeople() {
        this.peopleCollection.models.splice(0, this.peopleCollection.models.length);
        _.merge(this.peopleCollection.models, this.connections.map((c) => c.person));
        this.$injector.get('$rootScope').$applyAsync();
        this.emit(VideoProviderEvents.UpdatePeople, this.peopleCollection.models);
        const callInfo = selectCallInfo(this.$ngRedux.getState());
        if (this.isInGroupCall !== callInfo.isInGroupCall) {
            this.$ngRedux.dispatch(setIsGroupCallInf(this.isInGroupCall));
        }
    }
    findStreamByPatient(person) {
        return this.streams.find((stream) => stream.personConnection.person.uuid === person.uuid);
    }
    async startSession(config) {
        const { roomId, person, onSessionInit, mediaMode, mainSessionId } = config;
        this.sessionConfig = config;
        if (_.isFunction(onSessionInit))
            onSessionInit();
        this.resetUniqueConnectionsCount();
        getStatsAnalyzer.watchConnections(this);
        let joinTokens = undefined;
        try {
            const participants = [
                {
                    identityId: this.pn.getUuid(),
                    username: this.pn.globalState.username,
                },
                {
                    identityId: person.uuid,
                    username: person.state.username,
                },
            ];
            const data = await API.room.createSession({
                participants,
                roomId,
                mediaMode,
            });
            this.channel = data.sessionId;
            this.token = data.joinTokens.find((joinToken) => joinToken.identityId === this.pn.getUuid())?.token;
            this.ownershipToken = data.ownershipToken;
            this.apiKey = data.config.apiKey;
            joinTokens = data.joinTokens;
        }
        catch (error) {
            this.$rootScope.$emit(RootScopeEvents.ACTIVE_PROVIDER.CREATE_CHANNEL_ERROR, { error });
            throw error;
        }
        try {
            this.callSession = await this._createSession(this.channel, roomId, person, mainSessionId);
        }
        catch (error) {
            this.$rootScope.$emit(RootScopeEvents.ACTIVE_PROVIDER.CREATE_SESSION_ERROR, { error });
            throw error;
        }
        await this._initSession();
        this.checkParticipantAppear(person);
        this.$rootScope.$emit('activeProvider:createSession:success', {
            channel: this.channel,
            sessionId: this.callSession.id,
            roomId: roomId,
            mediaMode: mediaMode,
        });
        this.on(VideoProviderEvents.ConnectionCreated, (connection) => {
            if (!connection.person.state.pid)
                return;
            const CallSession = this.$injector.get('CallSession');
            CallSession.prototype$addPid({ id: this.callSession.id }, { pid: connection.person.state.pid }).$promise.catch((err) => {
                console.warn('Unable to add pid:', err);
            });
        });
        this.callSession.mediaMode = mediaMode;
        this.setSessionInfoToRedux(roomId);
        return {
            token: this.token,
            channel: this.channel,
            sessionId: this.callSession.id,
            apiKey: this.apiKey,
            joinTokens,
        };
    }
    changeAudioType(audioType) {
        this.debug('changeAudioType:', this.audioType, audioType);
        if (audioType && this.audioType !== audioType) {
            this.audioType = audioType;
            this.onAudioTypeChange();
        }
    }
    async addParticipantsToSession(people) {
        return API.room.joinSession({
            ownershipToken: this.ownershipToken,
            participants: people.map((person) => ({
                identityId: person.uuid,
                username: person.state.username,
            })),
        });
    }
    async joinSession(config) {
        const { sessionId, channel, onSessionInit, person, mediaMode, apiKey, joinToken, } = config;
        this.sessionConfig = config;
        this.channel = channel;
        this.apiKey = apiKey;
        this.callSession = { id: sessionId, mediaMode };
        if (_.isFunction(onSessionInit))
            onSessionInit();
        this.checkParticipantAppear(person);
        this.resetUniqueConnectionsCount();
        getStatsAnalyzer.watchConnections(this);
        this.setSessionInfoToRedux();
        this.token = joinToken;
        try {
            await this._joinSession(config);
            this.$rootScope.$emit(RootScopeEvents.ACTIVE_PROVIDER.JOIN_SESSION_SUCCESS, {
                channel: this.channel,
                sessionId: this.callSession.id,
            });
            return {
                token: this.token,
                channel: this.channel,
                sessionId: this.callSession.id,
                apiKey: this.apiKey,
            };
        }
        catch (error) {
            this.$rootScope.$emit(RootScopeEvents.ACTIVE_PROVIDER.JOIN_SESSION_ERROR, {
                channel: this.channel,
                sessionId: this.callSession.id,
            });
        }
    }
    setSessionInfoToRedux(roomId) {
        this.$ngRedux.dispatch(setCallSessionInf({
            id: this.callSession.id,
            isInCall: true,
        }));
        if (roomId) {
            const { url: roomUrl = undefined, isPersonal = undefined } = this.roomHelpersFactory.getRoom();
            this.$ngRedux.dispatch(setRoomInf({
                roomId,
                roomUrl,
                isPersonal,
            }));
        }
    }
    checkParticipantAppear(person) {
        if (person.state.onlyChat) {
            this.once(VideoProviderEvents.ConnectionCreated, () => {
                this.$rootScope.$emit('activeProvider:firstParticipantAppeared', {
                    sessionId: this.callSession.id,
                });
            });
        }
        else {
            this.once(VideoProviderEvents.StreamCreated, () => {
                this.$rootScope.$emit('activeProvider:firstParticipantAppeared', {
                    sessionId: this.callSession.id,
                });
            });
        }
    }
    onConfigUpdate() {
        this._onConfigUpdate();
    }
    clearCurrentDevices() {
        this.$ngRedux.dispatch(devices.actions.clearCurrentDevices());
    }
    onVideoQualityChange() {
        this._onConfigUpdate();
    }
    onAudioTypeChange() {
        this.$rootScope.$emit(RootScopeEvents.ACTIVE_PROVIDER.AUDIO_TYPE_CHANGED, this.audioType);
        this._onConfigUpdate();
    }
    _onConfigUpdate() { }
    async setSink(video) {
        const speakerId = devices.selectors.getCurrentSpeakerId(this.$ngRedux.getState());
        this.bindVideoElementToSpeaker(video, speakerId);
    }
    _createSession(channel, roomId, person, mainSessionId) {
        const Room = this.$injector.get('Room');
        const pn = this.$injector.get(AngularServices.Pn);
        return Room.sessions.create({
            id: roomId,
        }, {
            channel,
            userId: pn.getUserId(),
            internal: person.type === pn.getUserType(),
            isWeb: true,
            patientPlatform: 'web',
            doctorPlatform: 'web',
            videoProvider: this.name,
            connectionInfo: [],
            participantsCount: 2,
            pidList: person.state.pid ? JSON.stringify([person.state.pid]) : '',
            mainSessionId,
        }).$promise;
    }
    endSession() {
        const CallSession = this.$injector.get('CallSession');
        if (this.callSession && this.callSession.id) {
            const participantsCount = this.currentSessionUniqueConnections.length
                ? this.currentSessionUniqueConnections.length + 1
                : undefined;
            CallSession.prototype$end({ id: this.callSession.id }, {
                participantsCount,
                connectionInfo: this.callSession.connectionInfo,
            })
                .$promise.then()
                .catch((error) => {
                const metaData = {
                    Error: 'Request to CallSession.prototype$end not successful',
                    call_session: this.callSession,
                    participantsCount: participantsCount,
                    channel: this.channel,
                };
            });
        }
        this.lastSessionSettings = {
            callSession: _.clone(this.callSession),
            channel: this.channel,
        };
        this.ownershipToken = undefined;
        this.callSession = null;
        this._endSession();
        this.$ngRedux.dispatch(setCallSessionInf({
            id: '',
            isInCall: false,
        }));
        this.$ngRedux.dispatch(setIsGroupCallInf(false));
    }
    getCachedImgData() {
        return this.imgDataCache;
    }
    getSubscriberImgData(index) {
        return '';
    }
    getStreamImgData(streamId) {
        const stream = this.getStreamById(streamId);
        if (!stream)
            return '';
        return stream.getImgData();
    }
    getStreamById(streamId) {
        return this.streams.find((s) => s.id === streamId);
    }
    getCallSessionId() {
        if (!this.callSession) {
            console.error('Video provider error: API session not set.');
            return null;
        }
        return this.callSession.id;
    }
    async checkAudioStream() {
        return Promise.resolve();
    }
    async checkVideoStream() {
        if (this.isInCall)
            return;
        if (!this.ui.hasVideo)
            return;
        if (this.getUserMediaLastError)
            return;
        const imgFormer = await this.$timeout(() => this.getImgData(), 5000);
        const imgLatter = await this.$timeout(() => this.getImgData(), 7500);
        if (imgFormer === imgLatter)
            return Promise.reject();
    }
    getDevices() {
        return this._getDevices()
            .then(devices.helpers.getDevicesWithCondition)
            .then(devices.helpers.normalizeDevices);
    }
    // decided to keep it as child classes override this method, todo: remove it later
    async _getDevices() {
        return devices.selectors.getEnumerateDevices(this.$ngRedux.getState());
    }
    removeSessionEvents() {
        this.removeEvent(VideoProviderEvents.ConnectionCreated);
        this.removeEvent(VideoProviderEvents.ConnectionDestroyed);
        this.removeEvent(VideoProviderEvents.StreamCreated);
        this.removeEvent(VideoProviderEvents.StreamDestroyed);
        this.removeEvent(VideoProviderEvents.SessionDisconnected);
        this.removeEvent(VideoProviderEvents.SessionConnected);
        this.removeEvent(VideoProviderEvents.Exception);
        this.removeEvent(VideoProviderEvents.MOSChange);
        this.removeEvent(VideoProviderEvents.MOSUpdate);
    }
    getInitialCameraId() {
        return this.initialCameraIdPromise;
    }
    addSpectator(connectionId, spectator) { }
    getSubscriberElementId(stream) {
        if (stream)
            return `remote-video-subscriber-${stream.id}`;
    }
    getConnectionIdByStream(streamId) {
        return streamId;
    }
    getStreamIdByConnection(connectionId) {
        return connectionId;
    }
    resetUniqueConnectionsCount() {
        this.currentSessionUniqueConnections = [];
        this.on(VideoProviderEvents.StreamCreated, (stream) => {
            const uuid = _.get(stream, 'personConnection.person.uuid');
            if (!uuid || this.currentSessionUniqueConnections.includes(uuid))
                return;
            // Only session creator (doctor) can have connectionInfo
            if (this.callSession.connectionInfo) {
                this.callSession.connectionInfo.push(stream.personConnection.person.state.connection);
            }
            this.currentSessionUniqueConnections.push(uuid);
        });
    }
    normalizeError(error) {
        return error;
    }
    async enableWebcam() {
        this.debug('enableWebcam');
        const pn = this.$injector.get(AngularServices.Pn);
        const hasNoDevices = devices.selectors.hasNoDevices(this.$ngRedux.getState());
        if (hasNoDevices) {
            return Promise.reject({
                name: 'NotFoundError',
            });
        }
        let [err] = await to(this.initPublisher());
        if (!err) {
            pn.enableMediaAccess();
            this.getUserMediaLastError = null;
            this.emit(VideoProviderEvents.GetUserMediaSuccess);
            return;
        }
        err = this.normalizeError(err);
        this.startCameraAccessPoll();
        this.getUserMediaLastError = err;
        this.emit(VideoProviderEvents.GetUserMediaError, err);
        return Promise.reject(err);
    }
    async startCameraAccessPoll() {
        if (this.cameraAccessPollInterval)
            return;
        if (devices.selectors.hasCameraAndMicrophoneAccess(this.$ngRedux.getState()))
            return;
        this.cameraAccessPollInterval = this.$interval(async () => {
            await this.$ngRedux.dispatch(devices.actions.updateDevices());
            if (!this.localStream &&
                devices.selectors.hasCameraAndMicrophoneAccess(this.$ngRedux.getState())) {
                this.$interval.cancel(this.cameraAccessPollInterval);
                console.info('Camera access poll: terminated');
            }
        }, 2000);
        this.on(VideoProviderEvents.LocalStream, () => {
            if (this.$interval.cancel(this.cameraAccessPollInterval))
                console.info('Camera access poll: terminated on local stream');
        });
        console.info('Camera access poll: started');
    }
    findPnPersonByState(personSearchCriteria) {
        const pn = this.$injector.get(AngularServices.Pn);
        let person = pn.allJoinedPeople.findByUid(personSearchCriteria.uid);
        if (person)
            return person;
        this.$log.debug('Connection person not found', personSearchCriteria, this.getPeopleLogData(personSearchCriteria));
        person = generateUndeterminedPerson();
        person.uuid = personSearchCriteria.uid;
        return person;
    }
    getPeopleLogData(personSearch) {
        const pn = this.$injector.get(AngularServices.Pn);
        const props = _.uniq(['uid'].concat(Object.keys(personSearch)));
        return pn.people.models.map((person) => _.pick(person.state, props));
    }
    getMediaMode() {
        return this.callSession.mediaMode;
    }
    getApiKey() {
        return this.apiKey;
    }
    onStoreUpdate() {
        const state = this.$ngRedux.getState();
        this.checkDevicesChange(state);
        this.checkVideoDeviceChange(state);
        this.checkOutputDeviceChange(state);
        this.checkAudioDeviceChange(state);
    }
    checkDevicesChange(state) {
        const deviceHash = devices.selectors.getDevicesHash(state);
        if (this.deviceHash !== deviceHash) {
            // not first time
            const shouldTriggerUpdate = !!this.deviceHash;
            this.deviceHash = deviceHash;
            shouldTriggerUpdate && this.onDevicesChange();
        }
    }
    updateState(state) {
        const prevState = this.connectionState;
        this.connectionState = { ...this.connectionState, ...state };
        if (!shallowEqual(prevState, this.connectionState)) {
            this.onConnectionStateChange();
        }
    }
    notifyStreamStateChange() {
        // Rebuild streams so Angular sees a new reference when connection metadata changes
        this.updateStreams();
    }
    onConnectionStateChange() { }
    async onDevicesChange() {
        if (!this.localStream)
            return;
        const beforeCameraId = this.currentCameraId;
        const beforeMic = this.currentMic;
        const beforeMicId = this.currentMicId;
        await this.setCurrentDevices();
        this.debug(`devicechange: beforeCameraId: ${beforeCameraId} currentCameraId: ${this.currentCameraId} beforeMicId: ${beforeMicId}  currentMicId: ${this.currentMicId}`);
        if ((beforeCameraId && beforeCameraId !== this.currentCameraId) ||
            (beforeMic &&
                !devices.helpers.areDevicesEqual(beforeMic, this.currentMic))) {
            this._onConfigUpdate();
        }
    }
    checkVideoDeviceChange(state) {
        const currentCameraId = devices.selectors.getCurrentCameraId(state);
        if (this.currentCameraId !== currentCameraId) {
            // not first time and device exists
            const shouldTriggerUpdate = this.currentCameraId && currentCameraId;
            shouldTriggerUpdate && this.onVideoDeviceChange();
            this.currentCamera = devices.selectors.getCurrentCamera(state);
        }
    }
    onVideoDeviceChange() {
        const accessibleCameras = devices.selectors.getAccessibleCameras(this.$ngRedux.getState());
        if (accessibleCameras.length === 2) {
            this.$rootScope.$emit(RootScopeEvents.USER_ACTIONS.FLIP_CAMERA);
        }
        else {
            this.onConfigUpdate();
        }
        // we need this for video-session-end, todo: replace emit with react/redux
        this.$rootScope.$emit('changeVideoDevice');
    }
    checkOutputDeviceChange(state) {
        const currentSpeakerId = devices.selectors.getCurrentSpeakerId(state);
        if (this.currentSpeakerId !== currentSpeakerId) {
            // not first time and device exists
            const shouldTriggerUpdate = this.currentSpeakerId && currentSpeakerId;
            this.currentSpeaker = devices.selectors.getCurrentSpeaker(state);
            shouldTriggerUpdate && this.onOutputDeviceChange();
        }
    }
    onOutputDeviceChange() {
        if (!this.setSinkId.isSupported)
            return;
        this.getSubscriberVideoElements().forEach((video) => this.bindVideoElementToSpeaker(video, this.currentSpeaker.deviceId));
    }
    bindVideoElementToSpeaker(video, speakerDeviceId) {
        if (!this.setSinkId.isSupported)
            return;
        const hasSpeakerAccess = devices.selectors.hasSpeakerAccess(this.$ngRedux.getState());
        if (!hasSpeakerAccess || !callHelpers.doesVideoElHaveAudio(video))
            return;
        this.setSinkId(video, speakerDeviceId);
    }
    checkAudioDeviceChange(state) {
        const currentMicId = devices.selectors.getCurrentMicrophoneId(state);
        if (this.currentMicId !== currentMicId) {
            // not first time and device exists
            const shouldTriggerUpdate = this.currentMicId && currentMicId;
            this.currentMic = devices.selectors.getCurrentMicrophone(state);
            shouldTriggerUpdate && this.onAudioDeviceChange();
        }
    }
    onAudioDeviceChange() {
        this.onAudioConfigUpdate();
        // we need this for video-session-end, todo: replace emit with react/redux
        this.$rootScope.$emit('changeAudioDevice');
    }
    async startLiveCaptions() {
        const sessionId = this.getSessionId();
        if (!sessionId) {
            return;
        }
        try {
            const { captionsId } = await API.room.startLiveCaptions({ sessionId });
            this.captionsId = captionsId;
        }
        catch (error) {
            throw new Error('[Closed Captions]: Failed to start live captions', error);
        }
    }
    stopLiveCaptions() {
        if (!this.captionsId) {
            return;
        }
        API.room
            .stopLiveCaptions({ captionsId: this.captionsId })
            .then(() => {
            this.$rootScope.$emit(RootScopeEvents.CLOSED_CAPTIONS.STOPPED);
            return null;
        })
            .catch(() => {
            this.$rootScope.$emit(RootScopeEvents.CLOSED_CAPTIONS.ERROR_STOPPING);
        });
        // Reset captionsId after stopping
        this.captionsId = null;
    }
}
